import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  FirestoreDataConverter,
  FirestoreError,
  Query,
  Unsubscribe,
  WhereFilterOp,
  and,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  or,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./db";
import { SearchOptions } from "@/types/v2";
import { captureException } from "@/services/sentry";

/**
 * Handles a Firestore error by logging it to the console and reporting it
 * to Sentry -- a listener error here (e.g. a denied or dropped subscription)
 * otherwise fails silently: the callback simply never fires again, with no
 * visible error and nothing to alert on.
 * @param {FirestoreError} error - The Firestore error to be handled.
 */
const onError = (error: FirestoreError) => {
  console.error(error);
  captureException(error);
};

/**
 * Firestore utility for interacting with Firestore documents.
 * @namespace
 */
export const firestore = {
  getRef: <T = DocumentData>(path: string) => {
    return doc(db, path) as DocumentReference<T>;
  },
  /**
   * Retrieve a Firestore document from the specified path.
   * @param {string} path - The path to the Firestore document.
   * @param converter
   * @returns {Promise<T | undefined>} A Promise that resolves to the document data.
   */
  getDocument: async <T = DocumentData>(
    path: string,
    converter?: FirestoreDataConverter<T>
  ): Promise<T | undefined> => {
    const docRef = (
      converter ? doc(db, path).withConverter(converter) : doc(db, path)
    ) as DocumentReference<T>;
    const docSnap = await getDoc(docRef);
    return docSnap.data();
  },
  /**
   * Create a Firestore document at the specified path with the given data.
   * @param {string} path - The path where the document should be created.
   * @param {T} data - The data to be stored in the document.
   * @param converter
   * @returns {Promise<T>} A Promise that resolves to the created document data.
   */
  createDocument: async <T = DocumentData>(
    path: string,
    data: T,
    converter?: FirestoreDataConverter<T>
  ): Promise<T> => {
    const docRef = (
      converter ? doc(db, path).withConverter(converter) : doc(db, path)
    ) as DocumentReference<T>;
    const dataWId = { id: docRef.id, ...data };
    await setDoc(docRef, dataWId);
    return dataWId;
  },

  deleteDocument: async (path: string) => {
    const docRef = doc(db, path);
    await deleteDoc(docRef);
    return;
  },

  /**
   * Update a Firestore document at the specified path with the given data.
   * @param {string} path - The path of the document to be updated.
   * @param {T} data - The updated data for the document.
   * @param converter
   * @returns {Promise<T>} A Promise that resolves to the updated document data.
   */
  updateDocument: async <T = DocumentData>(
    path: string,
    data: T,
    converter?: FirestoreDataConverter<T>
  ): Promise<T> => {
    const docRef = (
      converter ? doc(db, path).withConverter(converter) : doc(db, path)
    ) as DocumentReference<T>;
    const dataWId = { id: docRef.id, ...data, _source: "client" };
    await updateDoc(docRef, dataWId);
    return dataWId;
  },

  /**
   * Atomically applies a set of write operations in a single Firestore batch.
   * All operations succeed or none do. Firestore caps batches at 500 ops.
   */
  batchWrite: async (
    ops: Array<
      | { type: "set"; path: string; data: object }
      | { type: "update"; path: string; data: object }
      | { type: "delete"; path: string }
    >
  ): Promise<void> => {
    const batch = writeBatch(db);
    ops.forEach((op) => {
      const ref = doc(db, op.path);
      if (op.type === "set") batch.set(ref, op.data);
      else if (op.type === "update") batch.update(ref, op.data);
      else batch.delete(ref);
    });
    await batch.commit();
  },

  queryDocuments: async <T = DocumentData>(
    path: string,
    searchParam: string,
    opsString: WhereFilterOp,
    equality: unknown,
    converter?: FirestoreDataConverter<T>,
    limitResults: number = 1000,
    orderByField?: string,
    orderDirection: "asc" | "desc" = "asc"
  ) => {
    let queryRef = query(
      collection(db, path),
      where(searchParam, opsString, equality)
    ) as Query<T>;

    queryRef = orderByField
      ? (query(
          queryRef,
          orderBy(orderByField, orderDirection),
          limit(limitResults)
        ) as Query<T>)
      : (query(queryRef, limit(limitResults)) as Query<T>);
    const queryRefwConv = converter
      ? queryRef.withConverter(converter)
      : queryRef;
    const querySnapshot = await getDocs(queryRefwConv);
    const docs: Array<T> = [];
    querySnapshot.forEach((doc) => docs.push({ ...doc.data() }));
    return docs;
  },
  listDocuments: async <T = DocumentData>(
    path: string,
    converter?: FirestoreDataConverter<T>,
    limitResults: number = 1000,
    orderByField?: string,
    orderDirection: "asc" | "desc" = "asc"
  ) => {
    const colRef = (
      converter
        ? collection(db, path).withConverter(converter)
        : collection(db, path)
    ) as Query<T>;
    const queryRef = orderByField
      ? (query(
          colRef,
          orderBy(orderByField, orderDirection),
          limit(limitResults)
        ) as Query<T>)
      : (query(colRef, limit(limitResults)) as Query<T>);
    const querySnapshot = await getDocs(queryRef);
    const docs: Array<T> = [];
    querySnapshot.forEach((doc) => docs.push({ ...doc.data() }));
    return docs;
  },
  /**
   * Reads every document in a collection, with no ordering and no limit.
   * Unlike `listDocuments`, each result carries its document id and nothing is
   * silently capped. Ordering is deliberately absent: an `orderBy` drops
   * documents that lack the ordered field. Only use this on small collections.
   * @template T - The type of the document data, defaults to `DocumentData`.
   * @param {string} path - The Firestore collection path.
   * @returns {Promise<Array<T>>} Every document, each with its `id` set to the document id.
   */
  listAllDocuments: async <T = DocumentData>(
    path: string
  ): Promise<Array<T>> => {
    const querySnapshot = await getDocs(collection(db, path));
    return querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })) as Array<T>;
  },
  /**
   * Fetches a paginated subset of documents from a specified Firestore collection.
   * @template T - The type of the document data, defaults to `DocumentData`.
   * @param {string} path - The Firestore collection path from which to fetch the documents.
   * @param {number} [pageSize] - The number of documents to fetch per page.
   * @param {DocumentSnapshot} [lastDocumentSnapshot] - A snapshot of the last document from the previous page to define the start point for the next page.
   * @param {FirestoreDataConverter<T>} [converter] - An optional Firestore data converter to map documents to specific formats.
   * @param {string} [orderByField] - The field by which to order the documents.
   * @param {"asc" | "desc"} [orderDirection] - The direction in which to order the documents, either ascending ("asc") or descending ("desc").
   * @returns {Promise<{
   *   docs: Array<T>;
   *   lastVisible: DocumentSnapshot<T>;
   *   hasMore: boolean;
   *   totalFetched: number;
   * }>} An object containing paginated documents, the last fetched document snapshot, a flag indicating if more documents are available, and the number of fetched documents
   *
   * - `docs`: Array of documents retrieved in the current page.
   * - `lastVisible`: The snapshot of the last visible document in the fetched data.
   * - `hasMore`: A boolean indicating if there are more documents to fetch.
   * - `totalFetched`: The count of documents fetched in the current page.
   */
  paginateDocuments: async <T = DocumentData>(
    path: string,
    pageSize: number = 20,
    lastDocumentSnapshot?: DocumentSnapshot,
    converter?: FirestoreDataConverter<T>,
    orderByField: string = "createdAt", // Default order field
    orderDirection: "asc" | "desc" = "desc"
  ): Promise<{
    docs: Array<T>;
    lastVisible: DocumentSnapshot<T>;
    hasMore: boolean;
    totalFetched: number;
    total?: number;
  }> => {
    const colRef = (
      converter
        ? collection(db, path).withConverter(converter)
        : collection(db, path)
    ) as Query<T>;

    let queryRef = query(
      colRef,
      orderBy(orderByField, orderDirection),
      limit(pageSize)
    ) as Query<T>;

    // Start after the last document from previous page
    if (lastDocumentSnapshot) {
      queryRef = query(queryRef, startAfter(lastDocumentSnapshot)) as Query<T>;
    }

    const querySnapshot = await getDocs(queryRef);
    const docs: Array<T> = [];
    querySnapshot.forEach((doc) =>
      docs.push({
        id: doc.id,
        ...doc.data(),
      })
    );

    return {
      docs,
      lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1],
      hasMore: querySnapshot.docs.length === pageSize,
      totalFetched: querySnapshot.docs.length,
    };
  },
  /**
   * Fetch and paginate documents from a Firestore collection with optional filters, ordering, and pagination controls.
   * @template T The type of the document data.
   * @param {string} path The path to the Firestore collection.
   * @param {number} [pageSize] The number of documents to fetch per page.
   * @param {DocumentSnapshot} [lastDocumentSnapshot] The last document snapshot from the previous page for pagination.
   * @param {FirestoreDataConverter<T>} [converter] The Firestore data converter to apply to the documents.
   * @param {string} [orderByField] The field to order the documents by.
   * @param {"asc" | "desc"} [orderDirection] The order direction, either ascending ("asc") or descending ("desc").
   * @param {Array<{searchParam: string; opsString: WhereFilterOp; equality: unknown;}>} [searchOptions] Array of search filters to apply to the query.
   * @returns {Promise<{docs: Array<T>; lastVisible: DocumentSnapshot<T>; hasMore: boolean; totalFetched: number; total?: number;}>} An object containing the fetched documents, the last visible document, a flag indicating if there are more documents, the total number of documents fetched, and an optional total count.
   */
  paginateDocumentsWithFilters: async <T = DocumentData>(
    path: string,
    pageSize: number = 20,
    lastDocumentSnapshot?: DocumentSnapshot,
    converter?: FirestoreDataConverter<T>,
    orderByField: string = "createdAt",
    orderDirection: "asc" | "desc" = "desc",
    searchOptions?: Array<{
      searchParam: string;
      opsString: WhereFilterOp;
      equality: unknown;
    }>
  ): Promise<{
    docs: Array<T>;
    lastVisible: DocumentSnapshot<T>;
    hasMore: boolean;
    totalFetched: number;
    total?: number;
  }> => {
    const colRef = (
      converter
        ? collection(db, path).withConverter(converter)
        : collection(db, path)
    ) as Query<T>;

    let queryRef = query(colRef) as Query<T>;

    // Add filters if provided
    if (searchOptions && searchOptions.length > 0) {
      if (searchOptions.length === 1) {
        // Single filter
        const { searchParam, opsString, equality } = searchOptions[0];
        queryRef = query(
          queryRef,
          where(searchParam, opsString, equality)
        ) as Query<T>;
      } else {
        // Multiple filters - use 'or' for different fields, 'and' for same field
        const whereConditions = searchOptions.map(
          ({ searchParam, opsString, equality }) =>
            where(searchParam, opsString, equality)
        );

        // If all filters are for different fields, use 'and' (default behavior)
        // If you need 'or' logic, you can use the 'or' function
        queryRef = query(queryRef, ...whereConditions) as Query<T>;
      }
    }

    // Add ordering and pagination
    queryRef = query(
      queryRef,
      orderBy(orderByField, orderDirection),
      limit(pageSize)
    ) as Query<T>;

    // Start after the last document from previous page
    if (lastDocumentSnapshot) {
      queryRef = query(queryRef, startAfter(lastDocumentSnapshot)) as Query<T>;
    }

    const querySnapshot = await getDocs(queryRef);
    const docs: Array<T> = [];
    querySnapshot.forEach((doc) =>
      docs.push({
        id: doc.id,
        ...doc.data(),
      })
    );

    return {
      docs,
      lastVisible: querySnapshot.docs[querySnapshot.docs.length - 1],
      hasMore: querySnapshot.docs.length === pageSize,
      totalFetched: querySnapshot.docs.length,
    };
  },

  /**
   * Retrieves the count of documents in a specified Firestore collection.
   * @param {string} path - The path to the Firestore collection.
   * @returns {Promise<number>} A promise that resolves to the count of documents in the collection.
   */
  getDocumentCount: async (path: string) => {
    const colRef = collection(db, path);
    const countSnapshot = await getCountFromServer(colRef);
    return countSnapshot.data().count;
  },

  /**
   * Retrieves the count of documents in a Firestore collection that match the specified search options.
   *
   * This function supports applying multiple search filters using Firestore's `where` clauses.
   * If the `getCountFromServer` method fails, a fallback method is used to count documents by retrieving them all (less efficient).
   * @param {string} path - The Firestore collection path.
   * @param {Array<{searchParam: string, opsString: WhereFilterOp, equality: unknown}>} searchOptions -
   *        Array of filter conditions where each condition includes:
   *        - `searchParam`: The field to filter by,
   *        - `opsString`: Comparison operator (e.g., "==", "!=", ">", etc.),
   *        - `equality`: The value to compare against.
   * @returns {Promise<number>} A promise that resolves to the count of documents matching the filters.
   * @throws {Error} Throws an error if the query or server operation fails.
   */
  getFilteredDocumentCount: async (
    path: string,
    searchOptions: Array<{
      searchParam: string;
      opsString: WhereFilterOp;
      equality: unknown;
    }>
  ): Promise<number> => {
    const colRef = collection(db, path);
    let queryRef = query(colRef);

    // Add filters
    if (searchOptions.length > 0) {
      if (searchOptions.length === 1) {
        // Single filter
        const { searchParam, opsString, equality } = searchOptions[0];
        queryRef = query(queryRef, where(searchParam, opsString, equality));
      } else {
        // Multiple filters - use 'and' logic
        const whereConditions = searchOptions.map(
          ({ searchParam, opsString, equality }) =>
            where(searchParam, opsString, equality)
        );
        queryRef = query(queryRef, ...whereConditions);
      }
    }

    // Use getCountFromServer for efficient counting when possible
    try {
      const countSnapshot = await getCountFromServer(queryRef);
      return countSnapshot.data().count;
    } catch (error) {
      // Fallback: if getCountFromServer doesn't work with the query,
      // fetch all documents and count them (less efficient but works)
      console.warn("Using fallback counting method due to:", error);
      const querySnapshot = await getDocs(queryRef);
      return querySnapshot.size;
    }
  },

  /**
   * Subscribes to changes in a Firestore document and invokes a callback when the document changes.
   * @param {string} path - The path to the Firestore document.
   * @param {(snapshot: DocumentSnapshot<DocumentData>) => void} callback - The callback function to be called when the document changes.
   * @param converter
   * @returns {Unsubscribe} A function that can be called to unsubscribe from further document changes.
   */
  subscribeDocument: <T = DocumentData>(
    path: string,
    callback: (doc: T | undefined) => void,
    converter?: FirestoreDataConverter<T>
  ): Unsubscribe => {
    const docRef = (
      converter ? doc(db, path).withConverter(converter) : doc(db, path)
    ) as DocumentReference<T>;
    return onSnapshot(docRef, (doc) => callback(doc.data()), onError);
  },

  /**
   * Subscribes to changes in a Firestore collection and invokes a callback when the documents change.
   * @param {string} path - The path to the Firestore collection.
   * @param {(array: Array<DocumentData>) => void} callback - The callback function to be called when the documents change.
   * @param converter
   * @param limitResults
   * @param orderByField
   * @param orderDirection
   * @returns {Unsubscribe} A function that can be called to unsubscribe from further document changes.
   */
  subscribeCollection: <T = DocumentData>(
    path: string,
    callback: (docs: T[]) => void,
    converter?: FirestoreDataConverter<T>,
    limitResults: number = 1000,
    orderByField?: string,
    orderDirection: "asc" | "desc" = "asc"
  ): Unsubscribe => {
    const colRef = converter
      ? collection(db, path).withConverter(converter)
      : (collection(db, path) as CollectionReference<T>);
    const queryRef = orderByField
      ? (query(
          colRef,
          orderBy(orderByField, orderDirection),
          limit(limitResults)
        ) as Query<T>)
      : (query(colRef, limit(limitResults)) as Query<T>);
    return onSnapshot(
      queryRef,
      (querySnapshot) => {
        const docs = querySnapshot.docs.map((doc) => doc.data());
        callback(docs);
      },
      onError
    );
  },

  /**
   * Subscribes to changes in a Firestore collection and invokes a callback when the documents change.
   * @param {string} path - The path to the Firestore collection.
   * @param {(array: Array<DocumentData>) => void} callback - The callback function to be called when the documents change.
   * @param searchParam
   * @param opsString
   * @param equality
   * @param converter
   * @param limitResults
   * @returns {Unsubscribe} A function that can be called to unsubscribe from further document changes.
   */
  subscribeCollectionWQuery: <T = DocumentData>(
    path: string,
    callback: (docs: T[]) => void,
    searchParam?: string,
    opsString?: WhereFilterOp,
    equality?: unknown,
    converter?: FirestoreDataConverter<T>,
    limitResults: number = 1000
  ): Unsubscribe => {
    const colRef = converter
      ? collection(db, path).withConverter(converter)
      : (collection(db, path) as CollectionReference<T>);
    if (searchParam && opsString && equality) {
      const queryRef = query(
        colRef,
        where(searchParam, opsString, equality),
        limit(limitResults)
      ) as Query<T>;
      return onSnapshot(
        queryRef,
        (snapshot) => {
          const docs = snapshot.docs.map((doc) => doc.data());
          callback(docs);
        },
        onError
      );
    }
    const queryRef = query(colRef, limit(limitResults)) as Query<T>;
    return onSnapshot(
      queryRef,
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => doc.data());
        callback(docs);
      },
      onError
    );
  },

  subscribeCollectionWQueries: <T = DocumentData>(
    path: string,
    callback: (docs: T[]) => void,
    searchOptions?: SearchOptions<T>[],
    converter?: FirestoreDataConverter<T>,
    limitResults: number = 1000,
    orderByField?: string,
    orderDirection: "asc" | "desc" = "asc",
    // Named distinctly from the shared module-level `onError` (not
    // `onError`): a same-named parameter here would shadow it for this
    // function's whole body, silently skipping the console.error/Sentry
    // report for every caller that (like most of them) doesn't pass one.
    onErrorCallback?: (error: FirestoreError) => void,
    searchType: "and" | "or" = "or"
  ): Unsubscribe => {
    const colRef = converter
      ? collection(db, path).withConverter(converter)
      : (collection(db, path) as CollectionReference<T>);

    let baseQuery = query(colRef, orderBy("id")) as Query<T>;

    if (searchOptions && searchType === "or") {
      baseQuery = query(
        baseQuery,
        or(
          ...searchOptions.map(({ searchParam, opsString, equality }) =>
            where(searchParam as string, opsString, equality)
          )
        )
      );
    }

    if (searchOptions && searchType === "and") {
      baseQuery = query(
        baseQuery,
        and(
          ...searchOptions.map(({ searchParam, opsString, equality }) =>
            where(searchParam as string, opsString, equality)
          )
        )
      );
    }
    baseQuery = orderByField
      ? query(
          baseQuery,
          orderBy(orderByField, orderDirection),
          limit(limitResults)
        )
      : query(baseQuery, limit(limitResults));

    return onSnapshot(
      baseQuery,
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => doc.data());
        callback(docs);
      },
      (error) => {
        onError(error);
        onErrorCallback?.(error);
      }
    );
  },

  /**
   * Subscribe to real-time changes in filtered document count
   */
  subscribeToFilteredDocumentCount: (
    path: string,
    searchOptions: Array<{
      searchParam: string;
      opsString: WhereFilterOp;
      equality: unknown;
    }>,
    callback: (count: number) => void
  ): Unsubscribe => {
    const colRef = collection(db, path);
    let queryRef = query(colRef);

    // Add filters
    if (searchOptions.length > 0) {
      if (searchOptions.length === 1) {
        // Single filter
        const { searchParam, opsString, equality } = searchOptions[0];
        queryRef = query(queryRef, where(searchParam, opsString, equality));
      } else {
        // Multiple filters
        const whereConditions = searchOptions.map(
          ({ searchParam, opsString, equality }) =>
            where(searchParam, opsString, equality)
        );
        queryRef = query(queryRef, ...whereConditions);
      }
    }

    return onSnapshot(
      queryRef,
      (snapshot) => {
        callback(snapshot.size);
      },
      onError
    );
  },
};
